// ─── Dine In board — table QR payloads ────────────────────────────────────

/**
 * The URL a table's QR code encodes: the plain dine-in deep link for that
 * table, matching App.tsx's `/dine-in/:businessId/:tableNumber` route.
 *
 * Deliberately carries NO session code, which is the single most important
 * decision about this feature. DineInShareHelper.buildDineInInviteLink appends
 * `?code=` so a person already at the table can pull a friend straight in —
 * that link is correct for sharing and WRONG for a QR, because a session code
 * belongs to one sitting. Bake one into something a restaurant prints or props
 * on a table and it works for exactly one group, then silently sends everyone
 * afterwards to a session that no longer exists.
 *
 * Without a code, the flow degrades correctly on its own: the first person to
 * scan creates the session and is shown the code to read out, and anyone
 * scanning later is asked for it. See DineInObject's header for that branch.
 *
 * Built from window.location.origin rather than href — this is generated from
 * the business page, so the current URL is the wrong base entirely.
 */
export function buildTableQrUrl(businessId: string, tableNumber: number): string {
  return `${window.location.origin}/dine-in/${businessId}/${tableNumber}`;
}

/** The same asset ShareProfileModal embeds, so every QR Nine generates is
 *  recognisably Nine's. Lives in /public, no import needed. */
export const NINE_QR_LOGO_SRC = "/nine_logo_vector_4.svg";

/**
 * qrcode.react imageSettings for the Nine logo, scaled to whatever size the QR
 * is being rendered at.
 *
 * 24% matches ShareProfileModal's 48px logo on a 200px code. That ratio is not
 * arbitrary: the logo punches a hole in the code (`excavate`), and error
 * correction has to reconstruct what's underneath. It only works because every
 * QR here is level="H" (~30% recoverable). Raise this, or drop a caller to a
 * lower level, and codes start failing to scan — on printed table tents, which
 * is the worst possible place to find out.
 *
 * A QR has exactly ONE centre image and this is it. Restaurant branding
 * belongs on the card around the code, not inside it.
 */
export function buildNineQrImageSettings(size: number) {
  const logoSize = Math.round(size * 0.24);
  return {
    src: NINE_QR_LOGO_SRC,
    height: logoSize,
    width: logoSize,
    excavate: true,
  };
}

export default { buildTableQrUrl, buildNineQrImageSettings, NINE_QR_LOGO_SRC };
